<template>
  <transition
    @enter="enter"
    @leave="leave"
  >
    <div
      v-show="active"
      class="card-container absolute top-50 left-50 flex flex-column justify-between shadow-3 z-999 br2 bg-white"
      style="transform: translateX(-50%)"
      ref="cardContainer"
    >
      <div>
        <div
          ref="cardImg"
          class="aspect-ratio aspect-ratio--6x4 z-1 br--top br2"
        >
          <div
            class="aspect-ratio--object cover bg-center overflow-hidden br--top br2"
            :style="'background-image: url(' + content.image + ')'"
          />
        </div>
        <div
          ref="closeButton"
          class="absolute shadow-3 pa1 br-100 pointer"
          :class="{
            'purple bg-white': content.brand === 'lh',
            'dark-gray bg-white': content.brand === 'lq',
          }"
          style="z-index: 1001; font-size: 0; top: -1.5rem; right: -1.5rem;"
          @click="close"
        >
          <svg
            width="2.5rem"
            height="2.5rem"
            viewBox="0 0 40 40"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0 20C0 8.94 8.94 0 20 0C31.06 0 40 8.94 40 20C40 31.06 31.06 40 20 40C8.94 40 0 31.06 0 20ZM20 17.18L25.18 12L28 14.82L22.82 20L28 25.18L25.18 28L20 22.82L14.82 28L12 25.18L17.18 20L12 14.82L14.82 12L20 17.18Z"
            />
          </svg>
        </div>
        <div
          ref="cardContent"
          class="ph3"
          style="padding-top: 18px"
        >
          <div
            class="fw6 f3 ttc mb2"
            v-html="content.title"
          />
          <div
            class="ttc pb2"
            v-html="content.location.location"
          />
          <div
            class="ttc mb2"
            v-html="content.location.postcode"
          />
          <div
            class="ttc mb2 green flex items-center"
            v-if="distance"
          >
            <simple-svg
              :filepath="icon.pin"
              class="w1 mr2"
            />
            <span v-if="distance">{{ distance }}</span>
            <div
              class="pl2"
            >
              miles from here
            </div>
          </div>
          <div class="f6 mb3 lh-copy flex flex-wrap">
            <Pill
              v-for="(item, index) in bedrooms"
              :key="index"
              class="mb2"
              :class="{
                'b--purple-deep purple-deep': content.brand === 'lh',
                'b--dark-gray dark-gray': content.brand === 'lq'
              }"
              :info="item + ' beds'"
            />
          </div>
        </div>
      </div>
      <div
        ref="cardListed"
        v-if="content.status === 'listed'"
        class="link tc w-100 br--bottom br2 dim"
        :class="{
          'bg-purple white': content.brand === 'lh',
          'bg-yellow dark-gray': content.brand === 'lq',
        }"
        style="padding: 0.7rem 0;"
        v-html="'Go to Development'"
        @click="$router.push(content.id)"
      />
      <div
        ref="cardListed"
        v-else
        class="link tc w-100 br--bottom br2 o-50"
        :class="{
          'bg-purple-inactive white': content.brand === 'lh',
          'bg-yellow-light dark-gray': content.brand === 'lq',
        }"
        style="padding: 0.7rem 0;"
        v-html="'Under Development'"
      />
    </div>
  </transition>
</template>

<script>
import Pill from '@/components/Pill'
import { gsap } from 'gsap'
export default {
  components: {
    Pill
  },
  props: {
    distance: {
      type: String,
      default: null
    },
    content: {
      type: Object,
      default: null
    },
    active: {
      type: Boolean,
      default: null
    }
  },
  data () {
    return {
      bedrooms: this.content.bedrooms.sort(),
      icon: {
        close: require('@/assets/img/off-close.svg'),
        pin: require('@/assets/img/icon-map-pin.svg')
      }
    }
  },
  methods: {
    close () {
      this.$emit('close')
    },
    leave (el, done) {
      const card = this.$refs.cardContainer
      const content = this.$refs.cardContent
      const listedButtons = this.$refs.cardListed
      const close = this.$refs.closeButton
      gsap.fromTo([card, content.children, listedButtons],
        {
          opacity: 1
        },
        {
          opacity: 0,
          delay: 0,
          duration: 0.3,
          onComplete: done
        }
      )
      gsap.to(close,
        {
          scale: 0
        }
      )
    },
    enter (el, done) {
      const card = this.$refs.cardContainer
      const cardImg = this.$refs.cardImg
      const close = this.$refs.closeButton
      const content = this.$refs.cardContent
      const listedButtons = this.$refs.cardListed
      const tl = gsap.timeline({})
      tl.to([content.children, listedButtons],
        {
          opacity: 0
        }
      ).fromTo(card,
        {
          opacity: 0
        },
        {
          opacity: 1,
          duration: 0.2,
          delay: 0
        }
      ).fromTo(cardImg,
        {
          paddingBottom: 0 + '%'
        },
        {
          paddingBottom: 66 + '%',
          duration: 0.2,
          delay: 0
        }
      ).fromTo(card,
        {
          y: -80 + 'px'
        },
        {
          y: -50 + '%',
          duration: 0.5,
          delay: 0,
          ease: 'expo.out'
        }
      ).fromTo(card,
        {
          height: 63 + 'px',
          width: 72 + 'px'
        },
        {
          width: 18.5 + 'rem',
          height: 28 + 'rem',
          duration: 0.5
        },
        '-=0.48'
      ).fromTo(listedButtons,
        {
          opacity: 0
        },
        {
          opacity: 1,
          delay: -0.1,
          duration: 0.2
        }
      ).fromTo(close,
        {
          scale: 0
        },
        {
          scale: 1,
          duration: 0.3,
          delay: -0.1,
          ease: 'back(1.7)'
        }
      ).fromTo(content.children,
        {
          opacity: 0,
          x: -10 + 'px'
        },
        {
          opacity: 1,
          x: 0 + 'px',
          stagger: 0.1,
          delay: 0.3,
          duration: 0.2,
          onComplete: done
        }
      )
    }
  }
}
</script>
